/**
 * Cookie utility for user preferences.
 * These are functional cookies (not tracking) — no consent required.
 * All cookies expire after 90 days and are scoped to this app's path.
 */

const COOKIE_EXPIRY_DAYS = 90;
const COOKIE_PREFIX = 'plm_';
const FILTER_PREFERENCE_VERSION_KEY = 'plm_filter_preference_version';
const FILTER_PREFERENCE_VERSION = '1';

/**
 * Check if user has accepted cookies.
 * The consent cookie itself is always allowed (strictly necessary).
 */
export function hasConsent() {
  return getCookie('cookie_consent') === 'accepted';
}

/**
 * Set a cookie value.
 * @param {string} name - Cookie name (auto-prefixed with plm_)
 * @param {string} value - Cookie value
 */
export function setCookie(name, value) {
  // Always allow the consent cookie itself; gate everything else on consent
  if (name !== 'cookie_consent' && !hasConsent()) {
    return;
  }
  if (value === null || value === undefined) {
    removeCookie(name);
    return;
  }
  const expires = new Date();
  expires.setDate(expires.getDate() + COOKIE_EXPIRY_DAYS);
  document.cookie = `${COOKIE_PREFIX}${name}=${encodeURIComponent(value)};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
}

/**
 * Get a cookie value.
 * @param {string} name - Cookie name (auto-prefixed with plm_)
 * @param {string} fallback - Default value if cookie not found
 * @returns {string|null}
 */
export function getCookie(name, fallback = null) {
  const fullName = `${COOKIE_PREFIX}${name}=`;
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const trimmed = cookie.trim();
    if (trimmed.startsWith(fullName)) {
      return decodeURIComponent(trimmed.substring(fullName.length));
    }
  }
  return fallback;
}

/**
 * Remove a cookie.
 * @param {string} name - Cookie name (auto-prefixed with plm_)
 */
export function removeCookie(name) {
  document.cookie = `${COOKIE_PREFIX}${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;SameSite=Lax`;
}

export function resetSavedFiltersOnce() {
  let version = null;
  try {
    version = window.localStorage.getItem(FILTER_PREFERENCE_VERSION_KEY);
  } catch {
    // Continue with the targeted cookie reset when localStorage is unavailable.
  }

  if (version === FILTER_PREFERENCE_VERSION) return;

  removeCookie('segment');
  removeCookie('category');

  try {
    window.localStorage.setItem(FILTER_PREFERENCE_VERSION_KEY, FILTER_PREFERENCE_VERSION);
  } catch {
    // The filter cookies are still cleared even when localStorage is unavailable.
  }
}
